<template>
  <a
    href="javascript:"
    class="in-console-training-trigger"
    @click="toggleSidebarState(SidebarContentTypes.IN_CONSOLE_TRAINING)"
    v-if="inConsoleTrainingExperiment && inEnabledPage"
  >
    {{ t("HELP") }}
  </a>
  <a
    href="javascript:"
    class="in-console-training-trigger"
    @click="toggleSidebarState(SidebarContentTypes.IN_CONSOLE_MESSAGES)"
    v-if="isWelcomeRoute && isInConsoleMessagesFeatureFlagEnabled"
  >
    <img
      :src="`${useAsset('img/merchant-console/envelope.svg')}`"
      alt="Envelope Icon"
    />
  </a>
</template>
<script setup lang="ts">
import useAsset from "@/apps/merchant3/shared-helpers/useAsset";
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import useFeatureFlag from "@/apps/merchant3/hooks/useFeatureFlag";
import AvailableFeatureFlags from "@/apps/merchant3/types/AvailableFeatureFlags";
import { useRoute } from "vue-router";
import { SidebarContentTypes } from "@/apps/merchant3/types/in-console/SidebarContentTypes";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { isFeatureFlagSet } = useFeatureFlag();

const currentPage = route.path;
const isWelcomeRoute = computed(() => {
  return route.name && route.name.toString().includes("M3_Welcome");
});
const inConsoleTrainingExperiment = computed(() => {
  return store.state.carts?.experiments?.InConsoleTraining
    ? store.state.carts?.experiments?.InConsoleTraining
    : false;
});

const isInConsoleMessagesFeatureFlagEnabled = computed(() => {
  return isFeatureFlagSet(AvailableFeatureFlags.IN_CONSOLE_MESSAGES);
});

const inEnabledPage = computed(() => {
  // Page suffixes that enable the in-console help feature
  const validPageSuffixes = [
    "/contract",
    "/customer-registration/customer-contact-info",
    "/customer-registration/terms-conditions",
    "/customer-registration/credit-check-permission",
    "/incode",
    "/offers",
    "/software",
    "/receipt",
  ];

  // Check if current path ends with any valid suffix
  if (
    currentPage.includes("/store") &&
    validPageSuffixes.some((suffix) => currentPage.endsWith(suffix))
  ) {
    return true;
  }

  // Check for parent path match if currentPage has dynamic ID
  const segments = currentPage.split("/").filter((p) => p !== "");
  if (segments.length === 0) return false;

  // Remove potential dynamic ID (last segment)
  const parentPath = "/" + segments.slice(0, -1).join("/");

  return (
    currentPage.includes("/store") &&
    validPageSuffixes.some((suffix) => parentPath.endsWith(suffix))
  );
});

const toggleSidebarState = (contentToShow: SidebarContentTypes) => {
  const isSmallScreen = ref<boolean>(window.innerWidth <= 480);
  const currentState =
    store.getters["ui/sidebarState"].sidebarIsOpen ?? !isSmallScreen.value;

  if (!currentState) {
    logEvent(EventLogs.Events.TRAINING_ARTICLES_SHOWN);
  } else {
    logEvent(EventLogs.Events.TRAINING_ARTICLES_HIDDEN);
  }

  store.dispatch("ui/setInConsoleSidebarState", {
    sidebarIsOpen: !currentState,
    contentToShow: contentToShow,
  });
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.in-console-training-trigger {
  color: $pj-color-granite-gray;
  font-size: 0.9rem;
  font-style: normal;
  font-weight: 600;
  margin-right: 39px;
  text-decoration: none;
}
</style>
